/**
 * Device Assignment Rule — Frontend ローカル Contract
 *
 * @standandforce/api-contract publish 前は BE contract をコピーして利用する。
 *
 * TODO(api-contract): publish 後は @standandforce/api-contract へ差し替え。
 * client.ts の import も更新すること。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
const RefSchema = z.object({
  id: z.string(),
  name: z.string()
});
const DeviceAssignmentRuleListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  deviceGroup: RefSchema,
  peopleGroups: z.array(RefSchema),
  ruleGroupId: z.string().nullable(),
  orderInGroup: z.number().nullable(),
  toBeAssignedCount: z.number()
});
const DeviceAssignmentRuleGroupListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  rules: z.array(DeviceAssignmentRuleListItemSchema)
});
const ListDeviceAssignmentRulesOutputSchema = z.object({
  ruleGroups: z.array(DeviceAssignmentRuleGroupListItemSchema),
  independentRules: z.array(DeviceAssignmentRuleListItemSchema)
});
const DeviceAssignmentRuleDetailSchema = DeviceAssignmentRuleListItemSchema.extend({
  deviceGroupId: z.string(),
  peopleGroupIds: z.array(z.string())
});
export const DeviceAssignmentRuleBodySchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  deviceGroupId: z.string(),
  peopleGroupIds: z.array(z.string()).min(1),
  ruleGroupId: z.string().nullable(),
  orderInGroup: z.number().int().positive().nullable()
});
const DeviceAssignmentRuleGroupBodySchema = z.object({
  name: z.string(),
  description: z.string().nullable()
});
const IdOutputSchema = z.object({
  id: z.string()
});
const PeopleGroupIdsQuerySchema = z.union([z.array(z.string()), z.string()]).optional().transform(value => {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
});
const ListDeviceAssignmentRulePeopleDataGridInputSchema = z.object({
  peopleGroupIds: PeopleGroupIdsQuerySchema,
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(2000).default(20),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  freeText: z.string().max(200).nullable().default(null)
});

/** Prisma `DeviceAssignmentAlertCategory` と完全一致（api-contract は domain import 不可） */
const DEVICE_ASSIGNMENT_ALERT_CATEGORY_VALUES = ['TO_BE_ASSIGNED', 'UNKNOWN_ASSIGNMENT'] as const;
const DeviceAssignmentAlertCategorySchema = z.enum(DEVICE_ASSIGNMENT_ALERT_CATEGORY_VALUES);
export type DeviceAssignmentAlertCategory = z.infer<typeof DeviceAssignmentAlertCategorySchema>;
const DataGridColumnSchema = z.object({
  fieldKey: z.string(),
  fieldName: z.string(),
  cellType: z.string(),
  sortable: z.boolean(),
  pinned: z.string().nullable(),
  minWidth: z.string().nullable()
});
const DataGridSchemaSchema = z.object({
  columns: z.array(DataGridColumnSchema),
  defaultSort: z.object({
    fieldKey: z.string(),
    direction: z.enum(['asc', 'desc'])
  }).nullable()
});
const DataGridItemSchema = z.object({
  id: z.string(),
  fieldValues: z.record(z.string(), z.unknown())
});
const DataGridResponseSchema = z.object({
  schema: DataGridSchemaSchema,
  items: z.array(DataGridItemSchema),
  count: z.number(),
  page: z.number(),
  maxPage: z.number()
});
const ListDeviceAssignmentAlertsDataGridInputSchema = z.object({
  category: DeviceAssignmentAlertCategorySchema,
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(2000).default(20),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  freeText: z.string().max(200).nullable().default(null),
  deviceGroupId: z.string().nullable().default(null),
  ruleId: z.string().nullable().default(null),
  personId: z.string().nullable().default(null)
});
const CountDeviceAssignmentAlertsOutputSchema = z.object({
  toBeAssignedCount: z.number(),
  unknownAssignmentCount: z.number()
});
export type CountDeviceAssignmentAlertsOutput = z.infer<typeof CountDeviceAssignmentAlertsOutputSchema>;
const ListDeviceAssignmentAlertTargetPersonsInputSchema = z.object({
  category: DeviceAssignmentAlertCategorySchema
});

/** 氏名整形は FE 責務のため、表示に必要な氏名パーツのみ返す（PERSON_NAME セルと同型） */
const AlertTargetPersonSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  middleName: z.string().nullable(),
  lastName: z.string()
});
const ListDeviceAssignmentAlertTargetPersonsOutputSchema = z.object({
  persons: z.array(AlertTargetPersonSchema)
});
export type ListDeviceAssignmentAlertTargetPersonsOutput = z.infer<typeof ListDeviceAssignmentAlertTargetPersonsOutputSchema>;
export const deviceAssignmentRuleContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/rules'
  }).input(z.object({})).output(ListDeviceAssignmentRulesOutputSchema),
  get: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/rules/{ruleId}'
  }).input(z.object({
    ruleId: z.string()
  })).output(DeviceAssignmentRuleDetailSchema),
  create: oc.route({
    method: 'POST',
    path: '/v2/device-assignment/rules'
  }).input(DeviceAssignmentRuleBodySchema).output(IdOutputSchema),
  update: oc.route({
    method: 'PUT',
    path: '/v2/device-assignment/rules/{ruleId}'
  }).input(DeviceAssignmentRuleBodySchema.extend({
    ruleId: z.string()
  })).output(IdOutputSchema),
  delete: oc.route({
    method: 'DELETE',
    path: '/v2/device-assignment/rules/{ruleId}'
  }).input(z.object({
    ruleId: z.string()
  })).output(z.object({
    id: z.string()
  })),
  listRulePeopleDataGrid: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/rules/people'
  }).input(ListDeviceAssignmentRulePeopleDataGridInputSchema).output(DataGridResponseSchema),
  createRuleGroup: oc.route({
    method: 'POST',
    path: '/v2/device-assignment/rule-groups'
  }).input(DeviceAssignmentRuleGroupBodySchema).output(IdOutputSchema),
  updateRuleGroup: oc.route({
    method: 'PUT',
    path: '/v2/device-assignment/rule-groups/{groupId}'
  }).input(DeviceAssignmentRuleGroupBodySchema.extend({
    groupId: z.string()
  })).output(IdOutputSchema),
  deleteRuleGroup: oc.route({
    method: 'DELETE',
    path: '/v2/device-assignment/rule-groups/{groupId}'
  }).input(z.object({
    groupId: z.string()
  })).output(z.object({
    id: z.string()
  })),
  reorderRuleGroup: oc.route({
    method: 'PUT',
    path: '/v2/device-assignment/rule-groups/{groupId}/order'
  }).input(z.object({
    groupId: z.string(),
    orderedRuleIds: z.array(z.string())
  })).output(z.object({
    id: z.string()
  })),
  listAlertsDataGrid: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/alerts/data-grid'
  }).input(ListDeviceAssignmentAlertsDataGridInputSchema).output(DataGridResponseSchema),
  listAlertTargetPersons: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/alerts/target-persons'
  }).input(ListDeviceAssignmentAlertTargetPersonsInputSchema).output(ListDeviceAssignmentAlertTargetPersonsOutputSchema),
  countAlerts: oc.route({
    method: 'GET',
    path: '/v2/device-assignment/alerts/counts'
  }).input(z.object({})).output(CountDeviceAssignmentAlertsOutputSchema)
});
export type ListDeviceAssignmentRulesOutput = z.infer<typeof ListDeviceAssignmentRulesOutputSchema>;
export type DeviceAssignmentRuleDetail = z.infer<typeof DeviceAssignmentRuleDetailSchema>;
export type DeviceAssignmentRuleBody = z.infer<typeof DeviceAssignmentRuleBodySchema>;
export type DeviceAssignmentRuleGroupBody = z.infer<typeof DeviceAssignmentRuleGroupBodySchema>;
export type DeviceAssignmentRuleListItem = z.infer<typeof DeviceAssignmentRuleListItemSchema>;